import { useMemo, useState } from 'react';
import { ArrowLeft, Copy, History, Plus, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { showErrorToast, showSuccessToast } from '@Helpers';
import { cn } from '@Utils';

import { Badge, ConfirmationModal, ErrorMessage, Loading } from '@Components';
import { Button } from '@Components/ui-v2/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@Components/ui-v2/select';
import { useHasPermission, useInvalidateScheduleLists, useScheduleTasks, useScheduleWeeks } from '@CustomHooks';
import { Permission, ScheduleDetail as ScheduleDetailModel, ScheduleWeekCustomizeSource, TaskType } from '@Models';
import { bopsApi } from '@Network';
import {
  getRetailerName,
  PAGE_FILTER_BAND_CLASS_NAME,
  PAGE_HEADER_BAND_CLASS_NAME,
  UPPERCASE_LABEL_CLASS_NAME,
} from '../../FieldServices/constants';
import { COMPACT_SELECT_TRIGGER_CLASS_NAME, getErrorDetail } from '../constants';
import AddTaskModal from './addTaskModal';
import {
  countPendingTasks,
  formatWeekOf,
  getAlertCeilingUtilization,
  getDistinctAreas,
  getListReadOnlyReason,
  getMayNotFitTaskIds,
  getNotContractedTaskIds,
  getReviewStatusFilter,
  getScheduleComposition,
  getScheduleWeeksRange,
  getTotalVisitSlots,
  getVisibleWeeks,
  isListLocked,
} from './constants';
import ScheduleNameEditor from './scheduleNameEditor';
import ScheduleSidebar from './scheduleSidebar';
import TaskTable from './taskTable';

interface ScheduleDetailProps {
  detail: ScheduleDetailModel;
  taskTypes: TaskType[];
  taskTypesById: Record<string, TaskType>;
  onScheduleChanged: () => Promise<void>;
}

const ALL_AREAS = 'all';

const ScheduleDetail = ({ detail, taskTypes, taskTypesById, onScheduleChanged }: ScheduleDetailProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canManage = useHasPermission(Permission.ManageSchedules);
  const {
    schedule,
    stores_count: storesCount,
    retailers,
    firms,
    plannable_task_type_codes: plannableTaskTypeCodes,
  } = detail;

  const [areaFilter, setAreaFilter] = useState<string>(ALL_AREAS);
  const [selectedWeekIndex, setSelectedWeekIndex] = useState<number | null>(null);
  const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isCopyingWeek, setIsCopyingWeek] = useState(false);

  const today = new Date();
  const weeksRange = getScheduleWeeksRange(today);
  const isWeeksQueryEnabled = getScheduleComposition(schedule.alert_ceiling_pct) !== 'alerts_only';
  const weeksQuery = useScheduleWeeks({
    scheduleId: schedule.id,
    from: weeksRange.from,
    to: weeksRange.to,
    enabled: isWeeksQueryEnabled,
  });
  const weeks = useMemo(
    () => (isWeeksQueryEnabled ? getVisibleWeeks(weeksQuery.data ?? []) : []),
    [isWeeksQueryEnabled, weeksQuery.data],
  );
  const selectedIndex = Math.min(selectedWeekIndex ?? 0, weeks.length - 1);
  const selectedWeek = selectedIndex >= 0 ? weeks[selectedIndex] : undefined;
  const isWeeksReady = !isWeeksQueryEnabled || weeksQuery.isSuccess || weeksQuery.isError;
  const isWeeksLoaded = !isWeeksQueryEnabled || weeksQuery.isSuccess;

  const weekStart = selectedWeek?.week_start ?? null;
  const tasksQuery = useScheduleTasks({ scheduleId: schedule.id, weekStart });
  const tasks = useMemo(() => tasksQuery.data?.items ?? [], [tasksQuery.data]);
  const invalidateLists = useInvalidateScheduleLists(schedule.id);

  const isTasksReady = tasksQuery.isSuccess;
  const isEmptyWeek = selectedWeek !== undefined && isTasksReady && tasks.length === 0;
  const isInheritedList = selectedWeek !== undefined && tasksQuery.data?.has_own_list === false && tasks.length > 0;
  const isLocked = isListLocked(tasks) && !isInheritedList;
  const hasNoVisitWeeksLeft = isWeeksQueryEnabled && weeksQuery.isSuccess && weeks.length === 0;
  const readOnlyReason = getListReadOnlyReason(selectedWeek, schedule, today, hasNoVisitWeeksLeft);
  const canEdit = canManage && isWeeksLoaded && isTasksReady && !isLocked && readOnlyReason === null;
  const canDraftFromLastWeek = selectedWeek?.has_earlier_own_list === true;
  const canCopyScheduleList = tasksQuery.data?.has_schedule_list === true;
  const roomSlots = selectedWeek
    ? getAlertCeilingUtilization(selectedWeek.effective_alert_ceiling_pct, getTotalVisitSlots(firms)).roomSlots
    : null;

  const eyebrow = useMemo(
    () =>
      [
        t('Schedule.Detail.Eyebrow'),
        selectedWeek?.label ?? (selectedWeek ? formatWeekOf(selectedWeek.week_start, i18n.language) : null),
        retailers.length > 0 ? getRetailerName(retailers[0] ?? '').toUpperCase() : null,
      ]
        .filter((part): part is string => part !== null && part !== '')
        .join(' · '),
    [i18n.language, retailers, selectedWeek, t],
  );

  const distinctAreas = useMemo(() => getDistinctAreas(tasks), [tasks]);
  const areaOptions = useMemo(
    () => [
      { value: ALL_AREAS, label: t('Schedule.Detail.Filters.AllAreas') },
      ...distinctAreas.map((area) => ({ value: area, label: area })),
    ],
    [distinctAreas, t],
  );
  const filteredTasks = useMemo(
    () => (areaFilter === ALL_AREAS ? tasks : tasks.filter((task) => task.area === areaFilter)),
    [tasks, areaFilter],
  );

  const plannableTaskTypes = useMemo(() => {
    const plannableCodes = new Set(plannableTaskTypeCodes);
    return taskTypes.filter((taskType) => plannableCodes.has(taskType.code));
  }, [plannableTaskTypeCodes, taskTypes]);

  const notContractedTaskIds = useMemo(
    () => getNotContractedTaskIds(tasks, plannableTaskTypes),
    [tasks, plannableTaskTypes],
  );

  const mayNotFitCount = useMemo(
    () => getMayNotFitTaskIds(tasks, roomSlots, isInheritedList, notContractedTaskIds).size,
    [tasks, roomSlots, isInheritedList, notContractedTaskIds],
  );

  let readOnlyLabel: string | null = null;
  if (isLocked) readOnlyLabel = t('Schedule.Detail.LockedBadge');
  else if (readOnlyReason) {
    readOnlyLabel = t(
      readOnlyReason === 'week_started' ? 'Schedule.Detail.StartedBadge' : 'Schedule.Detail.EndedBadge',
    );
  }

  const pendingCount = selectedWeek ? selectedWeek.pending_count : countPendingTasks(tasks, notContractedTaskIds);

  const handleTasksChanged = async () => {
    try {
      await invalidateLists();
    } catch (error) {
      showErrorToast(t('Schedule.Detail.ListRefreshError'));
    }
  };

  const handleCopyWeek = async (source: ScheduleWeekCustomizeSource) => {
    if (!selectedWeek) return;
    setIsCopyingWeek(true);
    try {
      await bopsApi.customizeScheduleWeek(schedule.id, selectedWeek.week_start, source);
      showSuccessToast(t('Schedule.Detail.CustomizeSuccess'));
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Detail.CustomizeError'));
    } finally {
      await handleTasksChanged();
      setIsCopyingWeek(false);
    }
  };

  const handleApprovePending = async () => {
    setIsApproving(true);
    try {
      const pendingTaskIds = tasks
        .filter((task) => getReviewStatusFilter(task.review_status) === 'pending' && !notContractedTaskIds.has(task.id))
        .map((task) => task.id);
      const approved = await bopsApi.bulkUpdateScheduleTaskReviewStatus(pendingTaskIds, 'approved', weekStart);
      showSuccessToast(t('Schedule.Detail.ApprovePendingSuccess', { count: approved.length }));
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Detail.ApprovePendingError'));
    } finally {
      await handleTasksChanged();
      setIsApproving(false);
      setIsApproveConfirmOpen(false);
    }
  };

  const renderTaskArea = () => {
    if (tasksQuery.isPending) return <Loading title={t('Schedule.Detail.Loading')} />;
    if (tasksQuery.isError) {
      return <ErrorMessage internalMessage={`Unable to get schedule tasks: ${String(tasksQuery.error)}`} />;
    }
    return (
      <TaskTable
        scheduleId={schedule.id}
        weekStart={weekStart}
        tasks={filteredTasks}
        allTasks={tasks}
        taskTypesById={taskTypesById}
        canManage={canEdit}
        roomSlots={roomSlots}
        isInheritedList={isInheritedList}
        notContractedTaskIds={notContractedTaskIds}
        onTasksChanged={handleTasksChanged}
      />
    );
  };

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className={cn('flex flex-wrap items-center gap-3', PAGE_HEADER_BAND_CLASS_NAME)}>
        <div className="flex min-w-0 flex-auto items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/schedules')}
            aria-label={t('Schedule.Detail.BackToList')}
            className="h-8 flex-shrink-0 border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50">
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">{t('Schedule.Detail.BackToList')}</span>
          </Button>
          <div className="min-w-0 flex-auto">
            <div className={cn('truncate', UPPERCASE_LABEL_CLASS_NAME)}>{eyebrow}</div>
            <ScheduleNameEditor
              scheduleId={schedule.id}
              name={schedule.name}
              canManage={canManage}
              onRenamed={onScheduleChanged}
            />
          </div>
        </div>
        <div className="ml-auto flex flex-shrink-0 items-center gap-3">
          <Select value={areaFilter} onValueChange={setAreaFilter}>
            <SelectTrigger
              aria-label={t('Schedule.Detail.Filters.Area')}
              className={cn(COMPACT_SELECT_TRIGGER_CLASS_NAME, 'w-auto min-w-[10rem] gap-2')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {areaOptions.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-xs">
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/schedules/${schedule.id}/configuration`)}
            className="h-9 flex-shrink-0 border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50">
            <Settings size={16} />
            {t('Schedule.Detail.Configuration')}
          </Button>
        </div>
      </div>

      {(canEdit || readOnlyLabel) && (
        <div className={cn('flex flex-wrap items-center gap-3', PAGE_FILTER_BAND_CLASS_NAME)}>
          {canEdit ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAddTaskOpen(true)}
                className="h-9 border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50">
                <Plus size={16} />
                {t('Schedule.Detail.AddTask.Button')}
              </Button>
              {isEmptyWeek && canCopyScheduleList && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isCopyingWeek}
                  onClick={() => handleCopyWeek('default')}
                  className="h-9 border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50">
                  <Copy size={16} />
                  {t('Schedule.Detail.CopyScheduleList')}
                </Button>
              )}
              {isEmptyWeek && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isCopyingWeek || !canDraftFromLastWeek}
                  onClick={() => handleCopyWeek('previous_week')}
                  className="h-9 border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50">
                  <History size={16} />
                  {t('Schedule.Detail.DraftFromLastWeek')}
                </Button>
              )}
              <span className="flex-1" />
              {pendingCount > 0 && (
                <Button
                  size="sm"
                  onClick={() => setIsApproveConfirmOpen(true)}
                  className="h-9 flex-shrink-0 text-xs font-semibold">
                  {t('Schedule.Detail.ApprovePendingButton', { count: pendingCount })}
                </Button>
              )}
            </>
          ) : (
            <div className="flex h-9 items-center">
              <Badge variant="gray" label={readOnlyLabel ?? ''} size="sm" className="rounded-full" />
            </div>
          )}
        </div>
      )}

      {/* The panel is a sibling of the scrolling task column, so it runs from the header to the
          bottom of the page instead of sitting inside the column's padding. */}
      <div className="flex min-h-0 w-full flex-1">
        <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-6 sm:px-7">{renderTaskArea()}</div>

        <ScheduleSidebar
          schedule={schedule}
          storesCount={storesCount}
          firms={firms}
          retailers={retailers}
          canManage={canManage}
          weeks={weeks}
          selectedWeekIndex={selectedIndex}
          onSelectWeek={setSelectedWeekIndex}
          isWeeksReady={isWeeksReady}
          hasWeeksError={weeksQuery.isError}
          mayNotFitCount={mayNotFitCount}
        />
      </div>

      {canManage && !isLocked && (
        <AddTaskModal
          isOpen={isAddTaskOpen}
          onClose={() => setIsAddTaskOpen(false)}
          scheduleId={schedule.id}
          weekStart={selectedWeek?.week_start ?? null}
          retailers={retailers}
          availableTaskTypes={plannableTaskTypes}
          onCreated={handleTasksChanged}
        />
      )}

      <ConfirmationModal
        isOpen={isApproveConfirmOpen && !isApproving}
        onClose={() => setIsApproveConfirmOpen(false)}
        onConfirm={handleApprovePending}
        confirmText={t('Schedule.Detail.ApprovePendingConfirm', { count: pendingCount })}
        buttonNamePrefix="SCHEDULE_DETAIL_APPROVE_PENDING"
      />
    </div>
  );
};

export default ScheduleDetail;
